import {
  CheckIcon,
  DashIcon,
  Divider,
  FormControlLabel,
  FormGroup,
  GKButton,
  GKCheckbox,
  GKTextField,
  Grid2,
  ResetIcon,
  RupeeIcon,
  SearchIcon,
  Slider,
  styled,
  Typography,
} from "components";
import { isNaN } from "lodash";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useAppSelector } from "store/store";

const CustomSlider = styled(Slider)(({ theme }) => ({
  color: "var(--primary-900)",
  height: 3,
  padding: "16px 0",
  "& .MuiSlider-thumb": {
    height: 22,
    width: 22,
    backgroundColor: "#E6F0F3",
    border: "2px solid currentColor",
    "&:hover": { boxShadow: "2px 2px 4px rgba(0, 0, 0, 0.25)" },
    "& .airbnb-bar": {
      height: 9,
      width: 1,
      backgroundColor: "currentColor",
      marginLeft: 1,
      marginRight: 1,
    },
  },
  "& .MuiSlider-track": { height: 3 },
  "& .MuiSlider-rail": {
    color: "#d8d8d8",
    opacity: 1,
    height: 3,
    ...theme.applyStyles("dark", { color: "#bfbfbf", opacity: undefined }),
  },
}));

export const PortfolioGroupFilter = ({ setOpen }: { setOpen: any }) => {
  const portfolioGroup = useAppSelector(
    (state) => state?.app?.portfolioGroupList,
  );

  const form = useFormContext();

  const [portfolioGroupFilter, setPortfolioGroupFilter] = useState(
    form.getValues("portfolioSelectionFilter.portfolio_group") || [],
  );

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={12} p={1.5}>
        <GKTextField
          size="small"
          slotProps={{
            input: {
              startAdornment: <SearchIcon />,
            },
          }}
          placeholder="Search Portfolio Group..."
        />
      </Grid2>
      <Grid2 size={12} px={1.5}>
        <Typography
          fontSize={12}
          fontWeight={500}
          color="#697586"
          display={"flex"}
          alignItems={"center"}
          gap={0.5}
        >
          Selected Portfolio Groups:
          <Typography fontSize={12} fontWeight={600} color="#44484F">
            {portfolioGroupFilter.length || 0}
          </Typography>
        </Typography>
      </Grid2>
      <Grid2 size={12} px={1.5} maxHeight={"320px"} overflow={"auto"}>
        <FormGroup>
          {portfolioGroup.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <GKCheckbox
                  disableLeftPadding
                  checked={portfolioGroupFilter.includes(option.value)}
                  onChange={() => {
                    const updatedOptions = portfolioGroupFilter.includes(
                      option.value,
                    )
                      ? portfolioGroupFilter.filter(
                          (item: any) => item !== option.value,
                        )
                      : [...portfolioGroupFilter, option.value];

                    setPortfolioGroupFilter(updatedOptions);
                  }}
                />
              }
              label={option.name}
            />
          ))}
        </FormGroup>
      </Grid2>
      <Grid2 size={12}>
        <Divider />
      </Grid2>
      <Grid2
        size={12}
        p={1.5}
        display={"flex"}
        justifyContent={"space-between"}
        gap={1}
      >
        <GKButton
          variant="outlined"
          startIcon={<ResetIcon />}
          fullWidth
          onClick={() => {
            form.setValue("portfolioSelectionFilter.portfolio_group", []);
            setOpen(false);
          }}
        >
          Clear all
        </GKButton>
        <GKButton
          variant="contained"
          startIcon={<CheckIcon />}
          fullWidth
          onClick={() => {
            form.setValue(
              "portfolioSelectionFilter.portfolio_group",
              portfolioGroupFilter,
            );
            setOpen(false);
          }}
        >
          Apply
        </GKButton>
      </Grid2>
    </Grid2>
  );
};

export const SliderFilter = ({
  setOpen,
  formKey,
  maxValue,
}: {
  setOpen: any;
  formKey: string;
  maxValue: number;
}) => {
  const form = useFormContext();

  // Min & Max range bounds
  const MIN_LIMIT = 0;
  const MAX_LIMIT = maxValue;

  // Form value
  const portfolioValue = form.getValues(formKey);

  // On first load, default to full range if portfolio_value is empty
  const [value, setValue] = useState<number[]>(
    Array.isArray(portfolioValue) && portfolioValue.length === 2
      ? portfolioValue
      : [MIN_LIMIT, MAX_LIMIT],
  );

  const handleInputChange = (index: 0 | 1, inputValue: string) => {
    const num = Number(inputValue);
    if (!isNaN(num)) {
      const newValue = [...value];
      newValue[index] = num;
      setValue(newValue);
    }
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      setValue(newValue);
    }
  };

  const handleClear = () => {
    const defaultRange = [MIN_LIMIT, MAX_LIMIT];
    setValue(defaultRange);
    form.setValue(formKey, []);
    setOpen(false);
  };

  const handleApply = () => {
    form.setValue(formKey, value);
    setOpen(false);
  };

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={12} p={1.5} display="flex" alignItems="center" gap={1}>
        <GKTextField
          size="small"
          type="number"
          value={value[0]}
          onChange={(e) => handleInputChange(0, e.target.value)}
          slotProps={{
            input: {
              endAdornment: <RupeeIcon />,
            },
          }}
        />
        <DashIcon />
        <GKTextField
          size="small"
          type="number"
          value={value[1]}
          onChange={(e) => handleInputChange(1, e.target.value)}
          slotProps={{
            input: {
              endAdornment: <RupeeIcon />,
            },
          }}
        />
      </Grid2>

      <Grid2 size={12} p={1.5} mx={1}>
        <CustomSlider
          value={value}
          onChange={handleSliderChange}
          getAriaValueText={(v) => `${v}₹`}
          min={MIN_LIMIT}
          max={MAX_LIMIT}
        />
      </Grid2>

      <Grid2
        size={12}
        p={1.5}
        display="flex"
        justifyContent="space-between"
        gap={1}
      >
        <GKButton
          variant="outlined"
          startIcon={<ResetIcon />}
          fullWidth
          onClick={handleClear}
        >
          Clear all
        </GKButton>

        <GKButton
          variant="contained"
          startIcon={<CheckIcon />}
          fullWidth
          onClick={handleApply}
        >
          Apply
        </GKButton>
      </Grid2>
    </Grid2>
  );
};
